import { computed, ref } from 'vue'
import type { Router } from 'vue-router'
import type { TourConfig, TourEnginePhase, TourResolveContext } from '../types/tour.types'
import { TOUR_CONFIG } from '../config/tour.config'
import { useTourProgressStore } from '../stores/tour-progress.store'
import { resolveTourForRoute, toursForRoute } from '../registry/tour-registry'
import { waitForElement, nextFrame } from './tour-navigation'
import { createStepAdvancer } from './useStepAdvance'

// ---- Module-level singleton state (shared by TourRoot and useTour) ----

const phase = ref<TourEnginePhase>('idle')
const activeTour = ref<TourConfig | null>(null)
const stepIndex = ref(0)
const targetEl = ref<Element | null>(null)

/** Set true when the user skips — TourRoot shows the "ask the AI" notice. */
const skipNoticeVisible = ref(false)
/** Id of the tour just completed — TourRoot shows a confirmation. */
const lastCompletedTourId = ref<string | null>(null)

let router: Router | null = null
let getContext: () => TourResolveContext = () => ({
  isAdmin: false,
  isImpersonated: false,
  isFlagEnabled: () => true,
})
let advancerCleanup: () => void = () => {}
let resolveToken = 0

// Watches for a state-dependent auto-start tour (e.g. List Builder's results view)
// to appear WITHOUT a route change. Bounded: only runs while idle on a route that
// still has an un-engaged, conditional, autoStart tour; disconnects on start.
let pendingObserver: MutationObserver | null = null
let watchedRoute: string | null = null

// Tour ids the user explicitly asked to replay this session (via "Tutorial for
// this screen"). Lets an already-engaged, state-conditional tour auto-appear once
// more. Consumed when the tour starts. Session-only, never persisted.
const forceOnce = new Set<string>()

const currentStep = computed(() => activeTour.value?.steps[stepIndex.value] ?? null)
const totalSteps = computed(() => activeTour.value?.steps.length ?? 0)
const isFirstStep = computed(() => stepIndex.value <= 0)
const isLastStep = computed(
  () => !!activeTour.value && stepIndex.value >= activeTour.value.steps.length - 1,
)
const isRunning = computed(() => phase.value === 'running' || phase.value === 'navigating')

export function initTourEngine(r: Router, contextProvider: () => TourResolveContext): void {
  router = r
  getContext = contextProvider
}

function teardown(): void {
  advancerCleanup()
  advancerCleanup = () => {}
  resolveToken++
  activeTour.value = null
  targetEl.value = null
  stepIndex.value = 0
  phase.value = 'idle'
  // Re-arm auto-start for the current route so a later state change (e.g. running
  // a search on List Builder) can start the next un-engaged tour.
  armAutoStart()
}

function clearPendingWatch(): void {
  pendingObserver?.disconnect()
  pendingObserver = null
}

/** Eligible to auto-start: an autoStart tour that's un-engaged OR force-replayed. */
function isEligible(tour: TourConfig): boolean {
  if (!tour.autoStart) return false
  // A replay the user asked for (help button → replayForRoute) always runs.
  if (forceOnce.has(tour.id)) return true
  const progress = useTourProgressStore()
  // Training mode off: tours never open on their own. Checked here rather than once when arming,
  // so the DOM watch for a state tour also stops if training is switched off while it waits.
  if (!progress.trainingModeAllScreens) return false
  return !progress.hasEngaged(tour.id)
}

/**
 * Starts the currently-active auto-start tour for the watched route if one is due;
 * otherwise, if a conditional auto-start tour is still pending, watches the DOM for
 * its state to appear.
 */
function armAutoStart(): void {
  clearPendingWatch()
  if (phase.value !== 'idle' || !watchedRoute) return
  // Only arm while genuinely on the watched route (guards teardown re-arm).
  if (router && router.currentRoute.value.name !== watchedRoute) return
  const ctx = getContext()
  if (ctx.isImpersonated) return

  const active = resolveTourForRoute(watchedRoute, ctx)
  if (active && isEligible(active)) {
    startTour(active)
    return
  }

  const hasPending = toursForRoute(watchedRoute, ctx).some(
    (t) => t.condition && isEligible(t),
  )
  if (!hasPending || typeof MutationObserver === 'undefined') return

  const route = watchedRoute
  pendingObserver = new MutationObserver(() => {
    if (phase.value !== 'idle') return
    const tour = resolveTourForRoute(route, getContext())
    if (tour && isEligible(tour)) {
      clearPendingWatch()
      startTour(tour)
    }
  })
  pendingObserver.observe(document.body, { childList: true, subtree: true })
}

async function resolveStep(): Promise<void> {
  const token = ++resolveToken
  advancerCleanup()
  advancerCleanup = () => {}

  const tour = activeTour.value
  const step = tour?.steps[stepIndex.value]
  if (!tour || !step) {
    finish()
    return
  }

  useTourProgressStore().setStepIndex(tour.id, stepIndex.value)

  // Cross-page: navigate first, let the DOM settle, then continue.
  if (step.route && router && router.currentRoute.value.name !== step.route) {
    phase.value = 'navigating'
    targetEl.value = null
    try {
      await router.push({ name: step.route })
    } catch {
      /* navigation aborted/duplicated — continue and try to resolve target */
    }
    await nextFrame()
    if (token !== resolveToken) return
  }

  // Resolve the highlighted element (waits for it to appear).
  if (step.target) {
    // Optional steps are "show only if present". Skip immediately when the target isn't already
    // in the DOM — otherwise the tour freezes for the full target-wait timeout with the PREVIOUS
    // step's element still spotlighted and no popover (e.g. an Actions card hidden by the user's
    // plan, or the onboarding "all done" banner before setup is complete). Such gated elements
    // are resolved by render time, so a synchronous check is enough.
    if (step.optional && !document.querySelector(step.target)) {
      advanceInternal()
      return
    }
    phase.value = 'navigating'
    const el = await waitForElement(step.target, { timeout: TOUR_CONFIG.targetWaitTimeout })
    if (token !== resolveToken) return
    if (!el && step.optional) {
      advanceInternal()
      return
    }
    targetEl.value = el
  } else {
    targetEl.value = null
  }

  phase.value = 'running'
  advancerCleanup = createStepAdvancer(step, targetEl.value, {
    onAdvance: () => advanceInternal(),
    router,
  })
}

function advanceInternal(): void {
  if (isLastStep.value) {
    finish()
  } else {
    stepIndex.value += 1
    void resolveStep()
  }
}

// ---- Public actions ----

export function startTour(tour: TourConfig, options: { fromStep?: number } = {}): void {
  clearPendingWatch()
  forceOnce.delete(tour.id) // consume any one-shot replay force
  skipNoticeVisible.value = false
  lastCompletedTourId.value = null
  activeTour.value = tour
  stepIndex.value = clampStep(tour, options.fromStep ?? 0)
  phase.value = 'running'
  void resolveStep()
}

export function resumeTour(tour: TourConfig): void {
  const progress = useTourProgressStore()
  startTour(tour, { fromStep: progress.lastStepIndex(tour.id) })
}

/**
 * Replay the full screen experience for a route: start the currently-matching tour
 * now, and let the route's other state-conditional tours auto-appear once more this
 * session (e.g. after running a search on List Builder), even if already completed.
 */
export function replayForRoute(routeName: string | null | undefined): void {
  if (!routeName) return
  const ctx = getContext()
  for (const t of toursForRoute(routeName, ctx)) {
    if (t.autoStart) forceOnce.add(t.id)
  }
  watchedRoute = routeName
  armAutoStart()
}

export function next(): void {
  advanceInternal()
}

export function prev(): void {
  if (isFirstStep.value) return
  stepIndex.value -= 1
  void resolveStep()
}

export function goToStep(index: number): void {
  if (!activeTour.value) return
  stepIndex.value = clampStep(activeTour.value, index)
  void resolveStep()
}

export function repeatStep(): void {
  void resolveStep()
}

export function restart(): void {
  stepIndex.value = 0
  void resolveStep()
}

export function pause(): void {
  advancerCleanup()
  advancerCleanup = () => {}
  phase.value = 'paused'
}

export function resume(): void {
  if (phase.value !== 'paused' || !activeTour.value) return
  phase.value = 'running'
  void resolveStep()
}

export function skip(): void {
  const tour = activeTour.value
  if (tour) useTourProgressStore().markSkipped(tour.id)
  teardown()
  skipNoticeVisible.value = true
}

export function dismissForever(): void {
  const tour = activeTour.value
  if (tour) useTourProgressStore().dismissForever(tour.id)
  teardown()
}

export function finish(): void {
  const tour = activeTour.value
  if (tour) {
    useTourProgressStore().markCompleted(tour.id)
    lastCompletedTourId.value = tour.id
  }
  teardown()
}

/**
 * User closed the tour (the X). Records a skip so it won't immediately re-auto-start,
 * but stays quiet (no "ask the AI" notice — that's reserved for the explicit Skip).
 */
export function close(): void {
  const tour = activeTour.value
  if (tour) useTourProgressStore().markSkipped(tour.id)
  teardown()
}

/** End the tour without recording completion/skip (e.g. AI hand-off / programmatic). */
export function stop(): void {
  teardown()
}

export function dismissSkipNotice(): void {
  skipNoticeVisible.value = false
}

export function clearCompletion(): void {
  lastCompletedTourId.value = null
}

/**
 * Called by the plugin's router.afterEach. Auto-starts a route's tour on first
 * visit, unless the user has engaged it, disabled training mode, or a tour is
 * already active. Never redirects.
 */
export function maybeAutoStart(routeName: string | null | undefined): void {
  watchedRoute = routeName ?? null
  // A tour running from the previous route stays; only (re)arm when idle.
  if (phase.value !== 'idle') {
    clearPendingWatch()
    return
  }
  armAutoStart()
}

function clampStep(tour: TourConfig, index: number): number {
  return Math.min(Math.max(index, 0), Math.max(0, tour.steps.length - 1))
}

export function useTourEngine() {
  return {
    // state
    phase,
    activeTour,
    stepIndex,
    targetEl,
    currentStep,
    totalSteps,
    isFirstStep,
    isLastStep,
    isRunning,
    skipNoticeVisible,
    lastCompletedTourId,
    // actions
    startTour,
    resumeTour,
    replayForRoute,
    next,
    prev,
    goToStep,
    repeatStep,
    restart,
    pause,
    resume,
    skip,
    finish,
    stop,
    close,
    dismissForever,
    dismissSkipNotice,
    clearCompletion,
    maybeAutoStart,
  }
}
